import { jsPDF } from 'jspdf';
// Brand wordmarks — extracted as vector artwork (Neue Frutiger World) from the
// official Philips template 7707-26_TV_ZG_Rueckgabeformular. They are rendered as
// images rather than typeset, exactly as in the reference PDF.
import ambilightWordmarkUrl from 'src/assets/images/returnform/ambilight_tv_wordmark.png';
import philipsWordmarkUrl from 'src/assets/images/returnform/philips_wordmark_vertical.png';
import { registerNeueFrutigerWorld, nfwCovers } from './neueFrutigerWorldFont';
import { localizeCountry } from 'src/scripts/utils/countries';

// Font families. Fixed copy (title, notice, labels) is set in the Philips
// corporate font; user-entered values always use the standard font.
const BRAND_FONT = 'NeueFrutigerWorld';
const STD_FONT = 'helvetica';

// Intrinsic pixel sizes of the wordmark PNGs → used to preserve aspect ratio.
const AMBILIGHT_RATIO = 198 / 1638;   // height / width
const PHILIPS_RATIO = 220 / 1092;     // width / height

// Philips corporate blue — used for the title and the thin table rules.
const PHILIPS_BLUE = [0, 74, 153];
const LABEL_COL_W = 44;     // mm, width of the left label column
const PAD_H = 3;            // mm, horizontal cell padding
const PAD_V = 3;            // mm, vertical cell padding
const LINE_H = 4.8;         // mm, line height for 10pt body text
const ROW_MIN = 13;         // mm, minimum height of a standard row
const ROW_MIN_TALL = 26;    // mm, minimum height of a tall row (address / retailer)

const TEXTS = {
  de: {
    title: 'Rückgabe Ihres Ambilight Fernsehers',
    // Notice is rendered as a flowing paragraph; the final sentence is bold.
    noticeNormal: 'Die Rückgabe Ihres Ambilight Fernsehers erfolgt ausschließlich über '
      + 'dieses Formular. Die Rückerstattung des Kaufpreises erfolgt ausschließlich '
      + 'persönlich beim Einzelhändler, bei dem Sie das Gerät erworben haben. Nach '
      + 'erfolgreicher Prüfung und Annahme der Rückgabe im Markt wird Ihnen der '
      + 'Kaufpreis vor Ort vom Händler erstattet. ',
    noticeBold: 'Eine nachträgliche Erstattung des Kaufpreises durch Philips ist nicht '
      + 'möglich.',
    labels: {
      betriebsnummer: 'Betriebsnummer',
      modellnummer: 'Modellnummer',
      seriennummer: 'Seriennummer',
      einzelhaendler: 'Einzelhändler',
      name: 'Name',
      nachname: 'Nachname',
      adresse: 'Adresse',
      email: 'E-Mail',
    },
    filename: 'Rueckgabeformular_Ambilight.pdf',
  },
  fr: {
    title: 'Retour de votre téléviseur Ambilight',
    noticeNormal: 'Le retour de votre téléviseur Ambilight s’effectue exclusivement au '
      + 'moyen de ce formulaire. Le remboursement du prix d’achat s’effectue '
      + 'exclusivement en personne auprès du revendeur chez qui vous avez acheté '
      + 'l’appareil. Après vérification et acceptation du retour en magasin, le prix '
      + 'd’achat vous sera remboursé sur place par le revendeur. ',
    noticeBold: 'Un remboursement ultérieur du prix d’achat par Philips n’est pas '
      + 'possible.',
    labels: {
      betriebsnummer: 'Numéro de dossier',
      modellnummer: 'Numéro de modèle',
      seriennummer: 'Numéro de série',
      einzelhaendler: 'Revendeur',
      name: 'Prénom',
      nachname: 'Nom',
      adresse: 'Adresse',
      email: 'E-mail',
    },
    filename: 'Formulaire_de_retour_Ambilight.pdf',
  },
};

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function readStash() {
  try {
    return JSON.parse(sessionStorage.getItem('ambilight_return_form') || '{}') || {};
  } catch (e) {
    return {};
  }
}

// Build the ordered [label, value(s)] rows. Empty strings render as blank cells
// meant to be completed by hand (Betriebsnummer, Einzelhändler).
function buildRows(t, data, lang) {
  const L = t.labels;
  const address = [
    [data.street, data.street_number].filter(Boolean).join(' '),
    [data.postcode, data.city].filter(Boolean).join(' '),
    localizeCountry(data.country, lang),
  ].filter((s) => s && s.trim());
  const retailer = [
    data.retailer,
    [data.retailer_street, data.retailer_street_number].filter(Boolean).join(' '),
    [data.retailer_postcode, data.retailer_city].filter(Boolean).join(' '),
    localizeCountry(data.retailer_country, lang),
  ].filter((s) => s && s.trim());

  return [
    { label: L.betriebsnummer, value: [data.vorgangsnummer || ''] },
    { label: L.modellnummer, value: [data.modelnumber || ''] },
    { label: L.seriennummer, value: [data.serialnumber || ''] },
    { label: L.einzelhaendler, value: retailer.length ? retailer : [''], tall: true },
    { label: L.name, value: [data.firstname || ''] },
    { label: L.nachname, value: [data.lastname || ''] },
    { label: L.adresse, value: address.length ? address : [''], tall: true },
    { label: L.email, value: [data.email || ''] },
  ];
}

// Flow a sequence of {text, bold} segments as a wrapped paragraph. Returns the
// number of lines. When draw is false it only measures (no output produced).
function flowRich(doc, segments, x, yTop, maxW, lineH, draw, family) {
  const fontFamily = family || STD_FONT;
  const words = [];
  segments.forEach((seg) => {
    seg.text.split(/(\s+)/).forEach((tok) => {
      if (tok !== '') words.push({ text: tok, bold: seg.bold });
    });
  });

  let cx = x;
  let line = 0;
  words.forEach((w) => {
    const isSpace = /^\s+$/.test(w.text);
    doc.setFont(fontFamily, w.bold ? 'bold' : 'normal');
    const ww = doc.getTextWidth(w.text);
    if (!isSpace && cx > x && cx + ww > x + maxW) {
      cx = x;
      line += 1;
    }
    if (isSpace && cx === x) return; // swallow leading space on a fresh line
    if (draw) {
      doc.text(w.text, cx, yTop + line * lineH + lineH * 0.8);
    }
    cx += ww;
  });
  return line + 1;
}

class ReturnFormPdf {
  init() {
    const btn = document.getElementById('download-return-form');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      this.generate().catch((err) => { console.error('PDF generation failed:', err); });
    });
  }

  async generate() {
    const lang = (document.documentElement.lang === 'fr') ? 'fr' : 'de';
    const t = TEXTS[lang];
    const data = readStash();

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    registerNeueFrutigerWorld(doc);
    const pageW = 210;
    const margin = 18;
    const tableW = 128;              // narrower than the page: leaves room for
    const tableRight = margin + tableW; // the vertical PHILIPS wordmark on the right

    // Fixed copy uses the Philips corporate font — but the embedded subset only
    // carries the glyphs of the German reference. If any fixed string (e.g. the
    // French text with accents) needs a glyph the subset lacks, fall back to the
    // standard font for the whole document so nothing renders as a blank box.
    const rows = buildRows(t, data, lang);
    const regularCopy = t.noticeNormal + rows.map((r) => r.label).join(' ');
    const boldCopy = t.title + t.noticeBold;
    const brandCovers = nfwCovers(regularCopy, 'normal') && nfwCovers(boldCopy, 'bold');
    const fixedFont = brandCovers ? BRAND_FONT : STD_FONT;

    // Load the brand wordmark artwork up front (falls back gracefully if missing).
    const [ambilightImg, philipsImg] = await Promise.all([
      loadImage(ambilightWordmarkUrl).catch(() => null),
      loadImage(philipsWordmarkUrl).catch(() => null),
    ]);

    // --- Header: "AMBILIGHT tv" wordmark (image) ---
    const headerTop = 16;
    const ambW = 95;
    const ambH = ambW * AMBILIGHT_RATIO;
    if (ambilightImg) {
      doc.addImage(ambilightImg, 'PNG', margin, headerTop, ambW, ambH);
    }

    // --- Title (Philips blue) ---
    let y = headerTop + ambH + 14;
    doc.setFont(fixedFont, 'bold');
    doc.setFontSize(16);
    doc.text(t.title, margin, y);
    y += 12;

    // --- Table ---
    doc.setDrawColor(...PHILIPS_BLUE);
    doc.setLineWidth(0.2);
    const tableTop = y;
    let cursorY = tableTop;

    // Notice cell (full width, left-aligned, last sentence bold)
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    const noticeSegs = [
      { text: t.noticeNormal, bold: false },
      { text: t.noticeBold, bold: true },
    ];
    const noticeW = tableW - 2 * PAD_H;
    const noticeLines = flowRich(doc, noticeSegs, 0, 0, noticeW, LINE_H, false, fixedFont);
    const noticeH = noticeLines * LINE_H + 2 * PAD_V;

    doc.line(margin, cursorY, tableRight, cursorY); // top rule
    flowRich(doc, noticeSegs, margin + PAD_H, cursorY + PAD_V, noticeW, LINE_H, true, fixedFont);
    cursorY += noticeH;
    doc.line(margin, cursorY, tableRight, cursorY); // rule below notice

    const kvTop = cursorY;
    const valueColX = margin + LABEL_COL_W;
    const valueColW = tableW - LABEL_COL_W;

    // Key/value rows
    rows.forEach((row) => {
      doc.setFontSize(10);
      // Value measurement uses the standard font (values are set in it).
      doc.setFont(STD_FONT, 'normal');
      const valueLines = row.value.reduce((acc, v) => acc.concat(
        doc.splitTextToSize(String(v), valueColW - 2 * PAD_H)), []);
      const contentH = Math.max(1, valueLines.length) * LINE_H + 2 * PAD_V;
      const rowH = Math.max(row.tall ? ROW_MIN_TALL : ROW_MIN, contentH);

      // Label — fixed copy → corporate font, normal weight, vertically centred
      doc.setFont(fixedFont, 'normal');
      doc.text(row.label, margin + PAD_H, cursorY + rowH / 2 + 1.4);
      // Value — user input → standard font, top-aligned for multi-line entries
      doc.setFont(STD_FONT, 'normal');
      doc.text(valueLines, valueColX + PAD_H, cursorY + PAD_V + LINE_H * 0.8);

      cursorY += rowH;
      doc.line(margin, cursorY, tableRight, cursorY); // rule below row
    });

    // Outer verticals (full table) + middle vertical (kv region only)
    doc.line(margin, tableTop, margin, cursorY);
    doc.line(tableRight, tableTop, tableRight, cursorY);
    doc.line(valueColX, kvTop, valueColX, cursorY);

    // --- Vertical PHILIPS wordmark down the right-hand side (image, pre-rotated) ---
    if (philipsImg) {
      const philH = Math.min(70, cursorY - tableTop);
      const philW = philH * PHILIPS_RATIO;
      const philX = pageW - margin - philW + 2;
      doc.addImage(philipsImg, 'PNG', philX, cursorY - philH, philW, philH);
    }

    doc.setProperties({ title: t.title });
    doc.save(t.filename);
  }
}

export default ReturnFormPdf;